<template>
    <div class="card-armory-Attack">
        <CardHeaderComponent :amount="this.amount" :name="this.name" :description="this.description" />
        <!-- TODO: Show generate resources from ships like Solar Satellites" -->
        <!-- <CardResourcesComponent /> -->
        <div class="card-requirements-container">
            <CardRequirementsComponent :resources="resourceRequeriments" :requeriments="this.requirementCollection" />
        </div>
        <div class="card-time-container">
            <ProgressBarComponent
                :totalTime="timeToProduce" 
                :elapsedTime="currentElapsedTime" 
                :elementsRemaining="currentShipsAmountLeft"
                :showProgressBar="isProducing"
                :callbackEvent="finishProduceEvent"
                />
        </div>
        <div v-if="!canBeProduce" class="card-alert-container">
            <AlertComponent :variant="alertVariantConstant.ERROR_ALERT">You do not meet the requirements to produce</AlertComponent>
        </div>
        <div class="card-button-container">
            <InputNumberFieldComponent :onClickEvent="produce" :disabled="!canBeProduce" v-model:value="this.shipsAmount" />
        </div>
    </div>
</template>

<style scoped>
.card-armory-Attack {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-radius: var(--border-radius-s);
    border: 1px solid var(--border-1);
    background: var(--background-1);
    overflow: hidden;
}

.card-requirements-container {
    display: flex;
    padding: 8px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    max-height: 300px;
}

.card-surface-title {
    display: flex;
    padding: 12px 16px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    background-color: var(--background-1);
}

.card-surface-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    height: min-content;
    overflow-y: auto;
    overflow-x: hidden;
}

.card-time-container {
    display: flex;
    padding: 0px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
}

.card-alert-container {
    display: flex;
    padding: 0px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
}

.card-button-container {
    display: flex;
    flex-grow: 1;
    height: 100%;
    padding: 16px;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    align-self: stretch;
}

.card-bg-container {
    display: flex;
    flex-grow: 1;
    height: 100%;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    align-self: stretch;
}
</style>

<script>
import { hoverEffect } from '@/composables/hoverEffectComposable';

import * as alertVariantConstant from '@/constants/alertVariantConstant';
import * as buttonConstant from '@/constants/buttonConstant';
import * as resourceConstant from '@/constants/resourceConstant';
import * as resourceStyleConstant from '@/constants/resourceStyleClassConstant';

import { userLoginDataStorage } from '@/storages/userLoginDataStorage';
import { userPlanetDataStorage } from '@/storages/userPlanetDataStorage';

import AlertComponent from './core/AlertComponent.vue';
import CardHeaderComponent from './card/CardHeaderComponent.vue';
// import CardResourcesComponent from './card/CardResourcesComponent.vue';
import CardRequirementsComponent from './card/CardRequirementsComponent.vue';
import InputNumberFieldComponent from './core/input/InputNumberFieldComponent.vue';
import ProgressBarComponent from './core/ProgressBarComponent.vue';

export default {
    name: 'AttackArmoryCardComponent',
    components: {
        AlertComponent,
        CardHeaderComponent,
        // CardResourcesComponent,
        CardRequirementsComponent,
        InputNumberFieldComponent,
        ProgressBarComponent,
    },
    setup() {
        hoverEffect();

        const userPlanetDataStorageInstance = userPlanetDataStorage();
        const userLoginDataStorageInstance = userLoginDataStorage();

        return {
            alertVariantConstant, 
            buttonConstant,
            resourceConstant,
            resourceStyleConstant,
            userPlanetDataStorageInstance,
            userLoginDataStorageInstance
        };
    },
    props: {
        shipTypeId: {
            type: Number,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        amount: {
            type: Number,
            required: true
        },
        buildData: {
            type: Object,
            required: true
        },
        requirementCollection: {
            type: Array,
            default: () => []
        },
        planetQueue: {
            type: [Object, null],
            default: null
        }
    },
    data() {
        return {
            planetId: this.userLoginDataStorageInstance.getUserMainPlanetId(),
            shipsAmount: ''
        }
    },
    computed: {
        syncPlanetQueue: {
            get() {
                return this.planetQueue
            },
            set(newValue) {
                this.$emit('update:planetQueue', newValue);
            }
        },
        selectedShipsAmount() {
            if (0 >= this.shipsAmount || isNaN(this.shipsAmount)) {
                return 1;
            }

            return this.shipsAmount;
        },
        isProducing() {
            return null !== this.syncPlanetQueue;
        },
        resourceRequeriments() {
            const metal = this.getMetal(this.buildData) * this.selectedShipsAmount;
            const crystal = this.getCrystal(this.buildData) * this.selectedShipsAmount;
            const deuterium = this.getDeuterium(this.buildData) * this.selectedShipsAmount;

            const userPlanetData = this.userPlanetDataStorageInstance.getPlanetById(this.planetId);

            return [
                {type: resourceConstant.METAL_RESOURCE, amount: metal, status: this.getResourceStatus(metal, userPlanetData.metal)},
                {type: resourceConstant.CRYSTAL_RESOURCE, amount: crystal, status: this.getResourceStatus(crystal, userPlanetData.crystal)},
                {type: resourceConstant.DEUTERIUM_RESOURCE, amount: deuterium, status: this.getResourceStatus(deuterium, userPlanetData.deuterium)},
            ];
        },
        canBeProduce() {
            let canBeProduce = true;

            for (const resource of this.resourceRequeriments) {
                if (resourceStyleConstant.NEGATIVE_RESOURCE_STYLE === resource.status) {
                    canBeProduce = false;
                    break;
                }
            }

            if (canBeProduce) {
                for (const requeriment of this.requirementCollection) {
                    if (false === requeriment.accomplish) {
                        canBeProduce = false;
                        break;
                    }
                }
            }

            return canBeProduce;
        },
        timeToProduce() {
            if (undefined === this.syncPlanetQueue?.finishAt) {
                
                return this.buildData?.secondsToBuild * this.selectedShipsAmount; 
            }

            const createdAt = new Date(this.syncPlanetQueue?.createdAt);
            const finishAt = new Date(this.syncPlanetQueue?.finishAt);
            
            return Math.round((finishAt - createdAt) / 1000);
        },
        currentElapsedTime() {
            if (undefined === this.syncPlanetQueue?.finishAt) {
                return 0;
            }

            const createdAt = new Date(this.syncPlanetQueue?.createdAt);
            const now = new Date();
            
            return Math.round((now - createdAt) / 1000);
        },
        currentShipsAmountLeft() {
            if (undefined === this.syncPlanetQueue?.extra) {
                return null;
            }

            return this.syncPlanetQueue.extra.shipAmount;
        }
    },
    methods: {
        getMetal(buildData) {
           return buildData?.metalPrice ?? 0;
        },
        getCrystal(buildData) {
            return buildData?.crystalPrice ?? 0;
        },
        getDeuterium(buildData) {
            return buildData?.deuteriumPrice ?? 0;
        },
        getResourceStatus(amount, minusCompareAmount) {
            if (0 > amount || undefined !== minusCompareAmount && minusCompareAmount < amount) {
                return resourceStyleConstant.NEGATIVE_RESOURCE_STYLE;
            }
            
            return resourceStyleConstant.DEFAULT_RESOURCE_STYLE;
        },
        async produce() {
            if (0 >= this.selectedShipsAmount) {
                return false;
            }

            try {
                const response = await this.$axios.buildShip(this.planetId, this.shipTypeId, this.selectedShipsAmount);
                console.log("Response data:", response.data);
                console.log("Response code:", response.status);
                console.log("Response headers:", response.headers);

                this.syncPlanetQueue = response.data;

                await this.$globalEvent.updateUserPlanetResourceView(this.planetId);
            } catch(error) {
                console.error("Error producing ships: ", error);
            }
        },
        finishProduceEvent() {
            this.syncPlanetQueue = null;
        }
    }
}
</script>